// Centralized place to store functionality that converts state into files for the end customer

import moment from 'moment';

const PART_PREFIX_PROFESSIONAL_SERVICES = '110-';
const PART_PREFIX_SOFTWARE = '130-';
const NO_VALUE_DISPLAY = 'Not Available';
const CSV_HEADER =
  '"Sales Order Num","PO Number","Sold To Customer","Actual Ship Date","Status","Item Number","Item Description","Serial Number","FSAN","MAC Address","Ship To Address"\n';

const getIdToLine = (linesData) =>
  linesData
    .filter((line) => line.lineId)
    .reduce((accumulator, line) => {
      accumulator[line.lineId] = line;
      return accumulator;
    }, {});

// Only returns parent lines with children ({id: [child lines] ...})
const getParentIdToChildren = (linesData) =>
  linesData
    .filter((line) => line.parentLineId)
    .reduce((accumulator, line) => {
      if (!accumulator[line.parentLineId]) {
        accumulator[line.parentLineId] = [];
      }

      accumulator[line.parentLineId].push(line);

      return accumulator;
    }, {});

const getNonParentHardwareLines = (linesData) => {
  const allLineIds = new Set(Object.keys(getIdToLine(linesData)));
  const allParentIds = new Set(Object.keys(getParentIdToChildren(linesData)));
  const childIds = allLineIds.difference(allParentIds);
  const psAndSoftwareExclusion = (line) =>
    !line.partNumber?.startsWith(PART_PREFIX_PROFESSIONAL_SERVICES) &&
    !line.partNumber?.startsWith(PART_PREFIX_SOFTWARE);

  return linesData.filter((line) => childIds.has(line.lineId)).filter(psAndSoftwareExclusion);
};

const getCsvEscapedValue = (value) => {
  // Escaping quotes that exist within value
  const escapedVal = value?.replaceAll ? value.replaceAll('"', '""') : value;
  return escapedVal || '';
};

const getShipToAddress = (shipAddress) => {
  if (!shipAddress) {
    return '';
  }

  return [
    shipAddress.address1,
    shipAddress.address2,
    shipAddress.city,
    shipAddress.state,
    shipAddress.province,
    shipAddress.zipcode,
    shipAddress.country,
  ]
    .filter((piece) => piece)
    .map((piece) => piece.trim())
    .join(', ');
};

const getLineDataCsv = (orderData) => {
  // We want to iterate over the lowest grain, what actually is shipping
  const nonParentLines = getNonParentHardwareLines(orderData.lines);
  const salesOrderNum = getCsvEscapedValue(orderData.salesOrderNumber);
  const poNumber = getCsvEscapedValue(orderData.billingInfo?.purchaseOrderNumber);
  const soldToCustomer = getCsvEscapedValue(orderData.billingInfo?.accountName);
  const shipToAddress = getCsvEscapedValue(getShipToAddress(orderData.shippingInfo?.address));

  return nonParentLines.reduce((csvString, line) => {
    // Putting a number in "=""NUMBER_HERE""" format causes excel to interpret the value as a string (not scientifically formatting it). Works in Google Sheets too
    const getStringFormattedValue = (value) => (value ? `=""${value}""` : NO_VALUE_DISPLAY);

    const getNewRow = (serialNumber, fsan, macAddress) => {
      return [
        `"${salesOrderNum}"`,
        `"${poNumber}"`,
        `"${soldToCustomer}"`,
        `"${line.actualShipDate ? moment(line.actualShipDate).format('YYYY-MM-DD') : ''}"`,
        `"${line.lineStatus}"`,
        `"${line.partNumber}"`,
        `"${getCsvEscapedValue(line.partDescription)}"`,
        `"${serialNumber}"`,
        `"${fsan || NO_VALUE_DISPLAY}"`,
        `"${macAddress || NO_VALUE_DISPLAY}"`,
        `"${shipToAddress}"`,
        '\n',
      ].join(',');
    };

    line.unitData?.forEach((unit) => {
      csvString += getNewRow(
        getStringFormattedValue(unit.serialNumber),
        getStringFormattedValue(unit.fsan),
        getStringFormattedValue(unit.macAddress)
      );
    });

    if (!line.unitData?.length) {
      csvString += getNewRow(NO_VALUE_DISPLAY, NO_VALUE_DISPLAY, NO_VALUE_DISPLAY);
    }

    return csvString;
  }, CSV_HEADER);
};

export { getLineDataCsv };
